import type { GenericSchema } from 'valibot'
import { nullable, parse, safeParse } from 'valibot'

interface Config<TSchema extends GenericSchema> {
  /**
   * The path to the Crystallize document.
   */
  path: MaybeRefOrGetter<string>

  /**
   * The Valibot schema to use for parsing the Crystallize document.
   */
  schema: TSchema

  /**
   * Optional route object, so we can use this in middleware (where useRoute does not work)
   */
  route?: ReturnType<typeof useRoute>
}

/**
 * Get reactive queryKey + queryFn that will fetch and parse a Crystallize catalogue.
 */
export function useCatalogueQueryOptions<TSchema extends GenericSchema>(config: Config<TSchema>) {
  const version = useCrystallizeVersion(config.route)

  const queryClient = useQueryClient()

  const queryKey = ['crystallize', version, config.path] as const

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- config.schema is the parser, not a real dep
  return queryOptions({

    queryKey,
    async queryFn({ queryKey }) {
      const [, version, path] = queryKey

      const data = await $fetch<{ catalogue?: unknown }>(`/api/crystallize/${version}${path}`)

      return parse(nullable(config.schema), data.catalogue)
    },
    initialData() {
      const [prefix, version, path] = queryKey

      const parentPath = toValue(path).split('/').slice(0, -1).join('/') || '/'

      const parentData = queryClient.getQueryData<{
        type: string
        children?: Array<{ path: string }>
      }>([prefix, version, parentPath])

      if (parentData?.type === 'folder') {
        const child = parentData.children?.find(item => item.path === toValue(config.path))
        const parsed = safeParse(config.schema, child)
        if (parsed.success) {
          return parsed.output as null
        }
      }

      return undefined
    },
  })
}
